/**
 * Slider.tsx — Generic carousel/slider powered by `react-multi-carousel`.
 *
 * JSON trigger: `uiCompType.method: "renderSlider"`
 *
 * **Slide sources (checked in order):**
 *  1. `sectionData.imageUrls` — Array of image URL strings; each renders as
 *     a `CustImage` with styles merged from `uiCompType.itemStyles`.
 *  2. `sectionData.panels`    — Array of `SectionData` objects; each is dispatched
 *     via `renderSection` (so any component type is supported as a slide).
 *  3. `sectionData.items`     — Same as `panels` but keyed differently.
 *
 * **Responsive sizing:**
 *  - Desktop: `containerStyles` ≠ `mContainerStyles` combination.
 *  - Mobile:  `mContainerStyles` + `mStyles` merged and applied.
 *  - Default container size: `{ height: '500px', width: '100%' }`.
 *
 * **Custom arrows:** MUI `NavigateNextIcon` / `NavigateBeforeIcon` styled via
 * `uiCompType.leftArrowStyles` and `uiCompType.rightArrowStyles`.
 *
 * **Carousel config:** Forward arbitrary ReactMultiCarousel props via
 * `uiCompType.options.carouselProps` (e.g. `autoPlay`, `infinite`,
 * `responsive`, `showDots`).
 *
 * **Performance:** Slide list and style objects are memoised to prevent
 * unnecessary re-renders.
 *
 * **Style slots:** `containerStyles`/`mContainerStyles`, `styles`/`mStyles`,
 * `itemStyles`/`mItemStyles`, `leftArrowStyles`, `rightArrowStyles`.
 */
import React, { useState, useEffect, useMemo, useCallback } from "react";
import ReactMultiCarousel from "react-multi-carousel";
import "react-multi-carousel/lib/styles.css";
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import cssStyles from "./cms_renderer.module.scss";
import { SectionData, ComponentRenderProps, renderFunctions, renderSection } from "./index";
import { useDeviceType } from "@/hooks/useDeviceType";

interface SliderProps {
    sectionData: SectionData;
    props: ComponentRenderProps;
}

const Slider = ({ sectionData, props }: SliderProps): JSX.Element | null => {
    //console.log("Slider - sectionData:", sectionData);

    const deviceType = useDeviceType();
    //console.log("Slider - deviceType:", deviceType);
    
    // Memoize style objects to prevent re-renders
    // TODO : need to use styles, instead of containerStyles alone for slider
    const dSliderStyles = useMemo(() => 
        sectionData.uiCompType?.containerStyles || sectionData.uiCompType?.styles || { 'height': '500px', 'width': "100%" }
    , [sectionData.uiCompType?.containerStyles, sectionData.uiCompType?.styles]);

    const mSliderStyles = useMemo(() => {
        const mContainer = sectionData.uiCompType?.mContainerStyles || {};
        const mStyles = sectionData.uiCompType?.mStyles || {};
        const mergedM = { ...mContainer, ...mStyles };

        return {...dSliderStyles, ...mergedM};
    }, [sectionData.uiCompType?.mContainerStyles, sectionData.uiCompType?.mStyles, sectionData.uiCompType?.containerStyles, sectionData.uiCompType?.styles]);

    //these two are item styles for each slide, will be merged with each item uiCompType.containerStyles.
    const dCommonSlideStyles = useMemo(() => 
        sectionData.uiCompType?.itemStyles || {}
    , [sectionData.uiCompType?.itemStyles]);
    
    const mCommonSlideStyles = useMemo(() => 
        sectionData.uiCompType?.mItemStyles || {}
    , [sectionData.uiCompType?.mItemStyles]);

    // Derive sliderStyles reactively from deviceType (replaces non-reactive useState + hacky DOM useEffect)
    const [sliderStyles, setSliderStyles] = useState(deviceType === "Mobile" ? mSliderStyles : dSliderStyles);

    // Sync sliderStyles when deviceType or source styles change
    useEffect(() => {
        setSliderStyles(deviceType === "Mobile" ? mSliderStyles : dSliderStyles);
    }, [deviceType, mSliderStyles, dSliderStyles]);

    // Memoize computed values
    const imageContainerStyles = useMemo(() => ({
        ...sectionData.uiCompType?.imageContainerStyles, 
        height: sectionData.uiCompType?.imageContainerStyles?.height || sliderStyles?.height 
    }), [sectionData.uiCompType?.imageContainerStyles, sliderStyles?.height]);
    
    const options = useMemo(() => sectionData.uiCompType?.options || {}, [sectionData.uiCompType?.options]);


    // Memoize slides to render
    const slidesToRender = useMemo(() => {
        if (sectionData.imageUrls && Array.isArray(sectionData.imageUrls)) {
            return sectionData.imageUrls;
        } else if (sectionData.items && Array.isArray(sectionData.items)) {
            
            const hasCommonMobileStyles = !!mCommonSlideStyles;

            // Merge item styles, preferring mobile item styles when present
            const mergedCommonSlideStyles = { ...dCommonSlideStyles, ...(hasCommonMobileStyles ? mCommonSlideStyles : {}) };
            // const mergedItemStyles = {
            //     ...mergedItemStyles,
            //     height: mergedItemStyles?.height ?? mergedContainerStyles?.height
            // };

            return (sectionData.items as SectionData[]).map((item: SectionData) => {
                // Create a deep copy to avoid mutation of original data
                const itemCopy = JSON.parse(JSON.stringify(item));

                itemCopy.uiCompType = itemCopy.uiCompType || {};

                itemCopy.uiCompType.containerStyles = { height: sliderStyles?.height, ...dCommonSlideStyles, ...(itemCopy.uiCompType.containerStyles || {}) };
                itemCopy.uiCompType.styles = { height: sliderStyles?.height, ...dCommonSlideStyles, ...(itemCopy.uiCompType.styles || {}) };

                if (deviceType === "Mobile") {
                    itemCopy.uiCompType.mContainerStyles = { height: sliderStyles?.height, ...mergedCommonSlideStyles, ...(itemCopy.uiCompType.mContainerStyles || {}) };
                    itemCopy.uiCompType.mStyles = { height: sliderStyles?.height, ...mergedCommonSlideStyles, ...(itemCopy.uiCompType.mStyles || {}) };
                }
                //console.log("Slider - slidesToRender - itemCopy:", itemCopy);
                return itemCopy;
            });
        }
        return [];
    }, [deviceType, sectionData.imageUrls, sectionData.items]);
    
    // Memoize carousel props
    const carouselProps = useMemo(() => (options as any)?.carouselProps || {}, [options]);

    const CustomRight = useCallback(({ onClick }: any) => (
        <button className={`${cssStyles["react-multiple-carousel__arrow"]} ${cssStyles["react-multiple-carousel__arrow--right"]}`} 
            onClick={onClick} style={{...sectionData.uiCompType?.rightArrowStyles}}>
            <NavigateNextIcon />
        </button>
    ), [sectionData.uiCompType?.rightArrowStyles]);
    
    const CustomLeft = useCallback(({ onClick }: any) => (
        <button className={`${cssStyles["react-multiple-carousel__arrow"]} ${cssStyles["react-multiple-carousel__arrow--left"]}`} 
            onClick={onClick} style={{...sectionData.uiCompType?.leftArrowStyles}}>
            <NavigateBeforeIcon />
        </button>
    ), [sectionData.uiCompType?.leftArrowStyles]);
    
    const panelClassName = "slider";

    const defaultResponsive = useMemo(() => ({                    
        desktop: {
            breakpoint: { max: 3000, min: 1024 },
            items: 1,
            slidesToSlide: 1,
            partialVisibilityGutter: 50,
            ...(options as any)?.carouselProps?.responsiveCust?.desktop
        },
        tablet: {
            breakpoint: { max: 1024, min: 464 },
            items: 1,
            slidesToSlide: 1,
            partialVisibilityGutter: 30,
            ...(options as any)?.carouselProps?.responsiveCust?.tablet
        },
        mobile: {
            breakpoint: { max: 464, min: 0 },
            items: 1,
            slidesToSlide: 1,
            partialVisibilityGutter: 20,
            ...(options as any)?.carouselProps?.responsiveCust?.mobile
        }
    }), [options]);

    const getCarouselChildren = useCallback((slides: any[], componentProps: ComponentRenderProps): JSX.Element[] => {
        if (sectionData.imageUrls) {
            return slides.map((imageUrl: string, index: number) => {
                const CustImageComponent = renderFunctions.CustImage;
                return <CustImageComponent sectionData={{
                    imageUrl,
                    uiCompType: {
                        containerStyles: imageContainerStyles
                    },
                } as SectionData} props={props} /> as JSX.Element;
            });
        } else if (sectionData.items) {
            const dItemContainerStyles = sectionData.uiCompType?.itemContainerStyles;
            const mItemContainerStyles = sectionData.uiCompType?.mItemContainerStyles;
            const hasItemContainer = !!(dItemContainerStyles || mItemContainerStyles);
            const resolvedItemContainerStyles = deviceType === "Mobile"
                ? { ...dItemContainerStyles, ...mItemContainerStyles }
                : dItemContainerStyles;

            return slides.map((item: SectionData, index: number) => {
                // Get merged container and item styles for this specific item
                const itemContainerStyles = item.uiCompType?.containerStyles || item.uiCompType?.styles || {};
                const itemItemStyles = item.uiCompType?.itemStyles || {};
                const slideContent = renderSection(`slide-item-${item.trackId || index}`, item, componentProps);

                return hasItemContainer ? (
                    <div key={`slide-${item.trackId || index}`} style={resolvedItemContainerStyles}>
                        {slideContent}
                    </div>
                ) : (
                    <React.Fragment key={`slide-${item.trackId || index}`}>
                        {slideContent}
                    </React.Fragment>
                );
            });
        } else {
            return [];
        }
    }, [sectionData.imageUrls, sectionData.items, imageContainerStyles, props]);
    return (
        <div id={'slider-' + (sectionData.trackId || '')} style={sliderStyles}>
            <ReactMultiCarousel
                afterChange={(previousSlide, { currentSlide, totalItems }) => {
                    // Force re-render of slide content after slide change
                    setTimeout(() => {
                        // Trigger a resize event to help components recalculate dimensions
                        window.dispatchEvent(new Event('resize'));
                    }, 100);
                }}
                beforeChange={(nextSlide, { currentSlide, totalItems }) => {}}
                responsive={carouselProps.responsive || defaultResponsive}
                partialVisible={false}
                ssr={true}
                deviceType={deviceType === "Mobile" ? "mobile" : "desktop"}
                showDots={slidesToRender.length > 1 && carouselProps.showDots !== false} 
                arrows={(slidesToRender.length > 1 && carouselProps.arrows !== false)}
                autoPlay={false}
                dotListClass={cssStyles["designer-carousel-dot-list"]}
                swipeable={true}
                keyBoardControl={true}
                itemClass="carousel-item-padding-40-px"
                customRightArrow={<CustomRight />}
                customLeftArrow={<CustomLeft />}
                infinite={true}
                containerClass="slider-container"
                sliderClass="slider-track"
                {...carouselProps}
            > 
                {getCarouselChildren(slidesToRender, props)}
            </ReactMultiCarousel>
        </div>
    );
}

export default Slider;


